import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Camera } from "lucide-react";
import { toast } from "sonner";

const slug = (s) => "pkt-" + s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function PackageCard({ p, idx, s, setS, onSaved }) {
  const { t } = useApp();
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const services = s.services || {};
  const routes = Object.entries(services).flatMap(([sk, sv]) => sv.routes.map((r) => ({ sk, r })));
  const update = (patch) => setS({ ...s, packages: s.packages.map((x, i) => (i === idx ? { ...x, ...patch } : x)) });
  const remove = () => { if (window.confirm(`${t("delete")} ${p.name}?`)) setS({ ...s, packages: s.packages.filter((_, i) => i !== idx) }); };
  const addTrip = () => routes[0] && update({ trips: [...p.trips, { service_type: routes[0].sk, route: routes[0].r }] });
  const setTrip = (ti, val) => { const [sk, r] = val.split("||"); update({ trips: p.trips.map((tr, i) => (i === ti ? { service_type: sk, route: r } : tr)) }); };
  const uploadImg = async (file) => {
    if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await onSaved(); const { data } = await api.post(`/packages/${p.id}/image`, fd); update({ image: data.url }); toast.success(t("saved")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("failed")); } finally { setBusy(false); }
  };
  return (
    <Card data-testid={`pkg-card-${p.id}`}><CardContent className="p-4 space-y-3">
      <div className="flex gap-3">
        <div className="w-28 h-24 rounded-xl bg-slate-100 overflow-hidden relative shrink-0">
          {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}
          <button type="button" onClick={() => ref.current.click()} disabled={busy} className="absolute inset-0 grid place-items-center bg-black/30 text-white opacity-0 hover:opacity-100 transition" data-testid={`pkg-img-btn-${p.id}`}><Camera className="w-5 h-5" /></button>
          <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { uploadImg(e.target.files[0]); e.target.value = ""; }} data-testid={`pkg-img-input-${p.id}`} />
        </div>
        <div className="flex-1 grid sm:grid-cols-3 gap-2">
          <div className="sm:col-span-2"><Label>{t("name")}</Label><Input value={p.name} onChange={(e) => update({ name: e.target.value })} data-testid={`pkg-name-${p.id}`} /></div>
          <div><Label>{t("discount")} (%)</Label><Input type="number" value={p.discount_percent} onChange={(e) => update({ discount_percent: +e.target.value })} data-testid={`pkg-disc-${p.id}`} /></div>
          <div><Label>{t("badge")}</Label><Input value={p.badge || ""} onChange={(e) => update({ badge: e.target.value })} data-testid={`pkg-badge-${p.id}`} /></div>
          <div className="sm:col-span-2"><Label>{t("description")}</Label><Textarea rows={2} value={p.desc || ""} onChange={(e) => update({ desc: e.target.value })} data-testid={`pkg-desc-${p.id}`} /></div>
        </div>
        <Button size="sm" variant="ghost" onClick={remove} data-testid={`pkg-del-${p.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
      </div>
      <div className="grid sm:grid-cols-4 gap-2 items-start rounded-xl bg-slate-50 p-3" data-testid={`pkg-pricing-${p.id}`}>
        <div><Label>{t("price_mode")}</Label>
          <Select value={p.price_mode || "percent"} onValueChange={(v) => update({ price_mode: v })}>
            <SelectTrigger className="h-8" data-testid={`pkg-mode-${p.id}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="percent">{t("price_mode_percent")}</SelectItem><SelectItem value="fixed">{t("price_mode_fixed")}</SelectItem></SelectContent>
          </Select></div>
        {(p.price_mode || "percent") === "fixed" ? (
          <div className="sm:col-span-3">
            <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
              {Object.entries(s.vehicle_types || {}).map(([vk, v]) => (
                <div key={vk}><Label className="text-xs">{v.label}</Label>
                  <Input type="number" className="h-8" placeholder="SAR" value={p.fixed_prices?.[vk] ?? ""} onChange={(e) => update({ fixed_prices: { ...(p.fixed_prices || {}), [vk]: e.target.value === "" ? undefined : Number(e.target.value) } })} data-testid={`pkg-fixed-${p.id}-${vk}`} /></div>))}
            </div>
            <p className="text-xs text-slate-500 mt-1">{t("fixed_price_hint")}</p>
          </div>
        ) : <div className="sm:col-span-3 text-xs text-slate-500 pt-6">{t("discount")}: {p.discount_percent}% {t("save_pct").replace("{n}", "").trim() && ""}</div>}
      </div>
      <div className="space-y-1">
        <Label>{t("trip")} ({p.trips.length})</Label>
        {p.trips.map((tr, ti) => (
          <div key={ti} className="flex gap-2 items-center">
            <Select value={`${tr.service_type}||${tr.route}`} onValueChange={(v) => setTrip(ti, v)}>
              <SelectTrigger className="h-8" data-testid={`pkg-trip-${p.id}-${ti}`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{routes.map((x) => <SelectItem key={`${x.sk}||${x.r}`} value={`${x.sk}||${x.r}`}>{services[x.sk].label} — {x.r}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="ghost" onClick={() => update({ trips: p.trips.filter((_, i) => i !== ti) })} data-testid={`pkg-trip-del-${p.id}-${ti}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
          </div>))}
        <Button size="sm" variant="outline" onClick={addTrip} data-testid={`pkg-trip-add-${p.id}`}><Plus className="w-4 h-4 mr-1" />{t("add_trip")}</Button>
      </div>
    </CardContent></Card>
  );
}

export default function PackagesAdmin({ s, setS, onSaved }) {
  const { t } = useApp();
  const [name, setName] = useState("");
  const add = () => {
    if (!name.trim()) return;
    const id = slug(name);
    if ((s.packages || []).some((p) => p.id === id)) return toast.error(t("package_exists"));
    setS({ ...s, packages: [...(s.packages || []), { id, name: name.trim(), badge: "", desc: "", discount_percent: 5, trips: [] }] }); setName("");
  };
  return (
    <div className="space-y-4">
      {(s.packages || []).map((p, i) => <PackageCard key={p.id} p={p} idx={i} s={s} setS={setS} onSaved={onSaved} />)}
      <Card><CardContent className="p-4 flex flex-wrap gap-2 items-center">
        <Input className="h-8 w-72" placeholder={t("new_package_ph")} value={name} onChange={(e) => setName(e.target.value)} data-testid="pkg-new-name" />
        <Button size="sm" variant="outline" onClick={add} data-testid="pkg-add"><Plus className="w-4 h-4 mr-1" />{t("add_package")}</Button>
        <span className="text-xs text-slate-500">{t("about_save_hint")}</span>
      </CardContent></Card>
    </div>
  );
}
